/**
 * TaskDetailRail – right-hand panel showing live task state.
 *
 * Tabs:
 *  1. Steps     – Timeline of agent execution steps
 *  2. Artifacts – Table of downloadable files / evidence
 *  3. Approvals – Pending/resolved approval requests
 *
 * Plus an inline FollowUpCard once the task reaches a terminal state.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge, Button, Card, Empty, Input,
  Skeleton, Space, Statistic, Table, Tabs, Tag,
  Timeline, Tooltip, Typography, message,
} from 'antd';
import {
  BellOutlined, CheckCircleOutlined, ClockCircleOutlined,
  CloseCircleOutlined, DownloadOutlined, FileOutlined,
  LoadingOutlined, ReloadOutlined,
} from '@ant-design/icons';
import type { AgentStep, Artifact, ApprovalRequest, TaskFollowUp } from '../types';
import { fetchTaskDetails, approveTaskRequest, rejectTaskRequest } from '../api/taskApiService';
import FollowUpCard from './FollowUpCard';
import { downloadArtifact } from '../api/client';

const { Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;

// ── Status colours ─────────────────────────────────────────────────────────────

const STEP_COLOR: Record<string, string> = {
  PENDING: '#6b7280',
  RUNNING: '#3b82f6',
  COMPLETED: '#10b981',
  FAILED: '#ef4444',
  SKIPPED: '#6b7280',
  WAITING_FOR_APPROVAL: '#f59e0b',
};

const TASK_STATUS_COLOR: Record<string, string> = {
  CREATED: 'default',
  PLANNING: 'processing',
  PLAN_READY: 'warning',
  RUNNING: 'processing',
  PAUSED: 'warning',
  WAITING_FOR_APPROVAL: 'warning',
  COMPLETED: 'success',
  FAILED: 'error',
  CANCELLED: 'default',
};

const TERMINAL_STATUSES = new Set(['COMPLETED', 'FAILED', 'CANCELLED']);

// ── Step dot ──────────────────────────────────────────────────────────────────

function stepDot(status: string): React.ReactNode {
  const color = STEP_COLOR[status] ?? '#6b7280';
  if (status === 'RUNNING') return <LoadingOutlined style={{ color, fontSize: 14 }} />;
  if (status === 'COMPLETED') return <CheckCircleOutlined style={{ color, fontSize: 14 }} />;
  if (status === 'FAILED') return <CloseCircleOutlined style={{ color, fontSize: 14 }} />;
  if (status === 'WAITING_FOR_APPROVAL') return <BellOutlined style={{ color, fontSize: 14 }} />;
  return <ClockCircleOutlined style={{ color, fontSize: 14 }} />;
}

// ── Approval card ─────────────────────────────────────────────────────────────

const ApprovalCard: React.FC<{
  approval: ApprovalRequest;
  onApprove: (note: string) => void;
  onReject: (note: string) => void;
}> = ({ approval, onApprove, onReject }) => {
  const [note, setNote] = useState('');
  const isPending = approval.status === 'PENDING';

  return (
    <Card
      size="small"
      style={{
        background: isPending ? '#1a1108' : '#161b22',
        borderColor: isPending ? '#d89614' : '#1e2736',
        marginBottom: 8,
      }}
    >
      <Space size={6} style={{ marginBottom: 6, flexWrap: 'wrap' }}>
        <BellOutlined style={{ color: '#f59e0b' }} />
        <Text strong style={{ color: '#e6edf3', fontSize: 12 }}>
          {approval.requested_action}
        </Text>
        <Tag color={isPending ? 'orange' : approval.status === 'APPROVED' ? 'success' : 'error'} style={{ fontSize: 10 }}>
          {approval.status}
        </Tag>
      </Space>
      {approval.reason && (
        <Paragraph style={{ color: '#c9d1d9', fontSize: 11, margin: '0 0 8px' }}>
          {approval.reason}
        </Paragraph>
      )}
      {isPending && (
        <>
          <TextArea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Optional note…"
            autoSize={{ minRows: 1, maxRows: 3 }}
            size="small"
            style={{ marginBottom: 8, background: '#0d1117', color: '#c9d1d9', border: '1px solid #2d333b' }}
          />
          <Space>
            <Button
              size="small"
              type="primary"
              onClick={() => onApprove(note)}
            >
              Approve
            </Button>
            <Button
              size="small"
              danger
              onClick={() => onReject(note)}
            >
              Reject
            </Button>
          </Space>
        </>
      )}
    </Card>
  );
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface Props {
  taskId: string | null;
  onFollowUp: (message: string) => void;
  followUpSubmitting?: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

const TaskDetailRail: React.FC<Props> = ({ taskId, onFollowUp, followUpSubmitting = false }) => {
  const [loading, setLoading] = useState(false);
  const [taskGoal, setTaskGoal] = useState('');
  const [taskStatus, setTaskStatus] = useState('');
  const [steps, setSteps] = useState<AgentStep[]>([]);
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [approvals, setApprovals] = useState<ApprovalRequest[]>([]);
  const [followUps, setFollowUps] = useState<TaskFollowUp[]>([]);
  const [activeTab, setActiveTab] = useState('steps');
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const taskStatusRef = useRef('');

  const load = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const details = await fetchTaskDetails(id);
      setTaskGoal(details.goal);
      setTaskStatus(details.status);
      taskStatusRef.current = details.status;
      setSteps(details.steps);
      setArtifacts(details.artifacts);
      setApprovals(details.approvals);
      setFollowUps(details.followUps);
    } catch {
      // silently ignore — will retry on next poll
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (!taskId) {
      setTaskGoal('');
      setTaskStatus('');
      taskStatusRef.current = '';
      setSteps([]);
      setArtifacts([]);
      setApprovals([]);
      setFollowUps([]);
      return;
    }
    void load(taskId);
    // Poll every 4 s while task is active
    pollingRef.current = setInterval(() => {
      if (!TERMINAL_STATUSES.has(taskStatusRef.current)) {
        void load(taskId);
      }
    }, 4000);
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [taskId, load]);

  const handleApprove = async (approvalId: string, note: string) => {
    try {
      await approveTaskRequest(approvalId, note);
      message.success('Approved');
      if (taskId) void load(taskId);
    } catch {
      message.error('Failed to approve');
    }
  };

  const handleReject = async (approvalId: string, note: string) => {
    try {
      await rejectTaskRequest(approvalId, note);
      message.success('Rejected');
      if (taskId) void load(taskId);
    } catch {
      message.error('Failed to reject');
    }
  };

  const pendingApprovals = approvals.filter(a => a.status === 'PENDING');

  if (!taskId) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#161b22',
        }}
      >
        <Empty
          description={
            <span style={{ color: '#555', fontSize: 12 }}>
              No active task. Send a message to start.
            </span>
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: '#161b22',
        borderLeft: '1px solid #21262d',
        overflow: 'hidden',
      }}
    >
      {/* Task header */}
      <div
        style={{
          padding: '12px 14px 10px',
          borderBottom: '1px solid #21262d',
          flexShrink: 0,
        }}
      >
        {loading && !taskGoal ? (
          <Skeleton active paragraph={{ rows: 1 }} title={{ width: '60%' }} />
        ) : (
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Space size={6} style={{ flexWrap: 'wrap' }}>
              <Tag color={TASK_STATUS_COLOR[taskStatus] ?? 'default'} style={{ fontSize: 10 }}>
                {taskStatus || 'LOADING'}
              </Tag>
              {pendingApprovals.length > 0 && (
                <Badge count={pendingApprovals.length} size="small" offset={[0, 0]}>
                  <Tag color="orange" style={{ fontSize: 10 }}>
                    Needs approval
                  </Tag>
                </Badge>
              )}
              <Tooltip title="Refresh">
                <Button
                  type="text"
                  size="small"
                  icon={<ReloadOutlined />}
                  loading={loading}
                  onClick={() => taskId && void load(taskId)}
                  style={{ color: '#8b949e', padding: 0 }}
                />
              </Tooltip>
            </Space>
            <Text
              style={{
                color: '#c9d1d9',
                fontSize: 12,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {taskGoal}
            </Text>

            {/* Progress stats */}
            {steps.length > 0 && (
              <Space size={16} style={{ marginTop: 4 }}>
                <Statistic
                  title={<span style={{ color: '#6b7280', fontSize: 10 }}>Steps</span>}
                  value={steps.filter(s => s.status === 'COMPLETED').length}
                  suffix={`/ ${steps.length}`}
                  valueStyle={{ fontSize: 14, color: '#10b981' }}
                />
                {artifacts.length > 0 && (
                  <Statistic
                    title={<span style={{ color: '#6b7280', fontSize: 10 }}>Files</span>}
                    value={artifacts.length}
                    valueStyle={{ fontSize: 14, color: '#3b82f6' }}
                  />
                )}
              </Space>
            )}
          </Space>
        )}
      </div>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        size="small"
        style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        tabBarStyle={{
          margin: 0,
          padding: '0 10px',
          background: '#161b22',
          borderBottom: '1px solid #21262d',
          flexShrink: 0,
        }}
        tabBarGutter={10}
      >
        {/* ── Steps ── */}
        <TabPane
          tab={
            <span style={{ fontSize: 12, color: '#c9d1d9' }}>
              Steps
              {steps.length > 0 && (
                <Tag style={{ marginLeft: 4, fontSize: 10 }}>{steps.length}</Tag>
              )}
            </span>
          }
          key="steps"
          style={{ flex: 1, overflow: 'auto', padding: '12px 14px' }}
        >
          {loading && steps.length === 0 ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : steps.length === 0 ? (
            <Empty
              description={<span style={{ color: '#555', fontSize: 12 }}>No steps yet</span>}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <Timeline mode="left">
              {steps.map(step => (
                <Timeline.Item
                  key={step.id}
                  dot={stepDot(step.status)}
                  color={STEP_COLOR[step.status] ?? '#6b7280'}
                >
                  <div>
                    <Text
                      strong
                      style={{
                        color: '#e6edf3',
                        fontSize: 12,
                        display: 'block',
                        marginBottom: 2,
                      }}
                    >
                      {step.name}
                    </Text>
                    {step.description && (
                      <Text style={{ color: '#8b949e', fontSize: 11 }}>
                        {step.description}
                      </Text>
                    )}
                    {step.error_message && (
                      <Text style={{ color: '#ef4444', fontSize: 11, display: 'block', marginTop: 2 }}>
                        {step.error_message}
                      </Text>
                    )}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          )}
        </TabPane>

        {/* ── Artifacts ── */}
        <TabPane
          tab={
            <span style={{ fontSize: 12, color: '#c9d1d9' }}>
              Files
              {artifacts.length > 0 && (
                <Tag style={{ marginLeft: 4, fontSize: 10 }}>{artifacts.length}</Tag>
              )}
            </span>
          }
          key="artifacts"
          style={{ flex: 1, overflow: 'auto', padding: '12px 14px' }}
        >
          {loading && artifacts.length === 0 ? (
            <Skeleton active paragraph={{ rows: 3 }} />
          ) : artifacts.length === 0 ? (
            <Empty
              description={<span style={{ color: '#555', fontSize: 12 }}>No files yet</span>}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <Table
              size="small"
              dataSource={artifacts}
              rowKey="id"
              pagination={false}
              style={{ background: 'transparent' }}
              columns={[
                {
                  title: 'File',
                  dataIndex: 'filename',
                  key: 'filename',
                  render: (name: string) => (
                    <Space size={6}>
                      <FileOutlined style={{ color: '#8b949e' }} />
                      <Text style={{ color: '#c9d1d9', fontSize: 12 }}>{name}</Text>
                    </Space>
                  ),
                },
                {
                  title: 'Size',
                  dataIndex: 'size_bytes',
                  key: 'size_bytes',
                  width: 60,
                  render: (b: number) => (
                    <Text style={{ color: '#6b7280', fontSize: 11 }}>
                      {b > 1_048_576 ? `${(b / 1_048_576).toFixed(1)} MB` : `${Math.ceil(b / 1024)} KB`}
                    </Text>
                  ),
                },
                {
                  title: '',
                  key: 'actions',
                  width: 36,
                  render: (_: any, artifact: Artifact) => (
                    <Tooltip title="Download">
                      <Button
                        type="text"
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => downloadArtifact(artifact.id, artifact.filename)}
                        style={{ color: '#8b949e' }}
                      />
                    </Tooltip>
                  ),
                },
              ]}
            />
          )}
        </TabPane>

        {/* ── Approvals ── */}
        <TabPane
          tab={
            <span style={{ fontSize: 12, color: '#c9d1d9' }}>
              Approvals
              {pendingApprovals.length > 0 && (
                <Badge count={pendingApprovals.length} size="small" style={{ marginLeft: 4 }} />
              )}
            </span>
          }
          key="approvals"
          style={{ flex: 1, overflow: 'auto', padding: '12px 14px' }}
        >
          {loading && approvals.length === 0 ? (
            <Skeleton active paragraph={{ rows: 3 }} />
          ) : approvals.length === 0 ? (
            <Empty
              description={<span style={{ color: '#555', fontSize: 12 }}>No approval requests</span>}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            approvals.map(a => (
              <ApprovalCard
                key={a.id}
                approval={a}
                onApprove={note => handleApprove(a.id, note)}
                onReject={note => handleReject(a.id, note)}
              />
            ))
          )}
        </TabPane>

        {/* ── Follow-ups ── */}
        {followUps.length > 0 && (
          <TabPane
            tab={
              <span style={{ fontSize: 12, color: '#c9d1d9' }}>
                Follow-ups
                <Tag style={{ marginLeft: 4, fontSize: 10 }}>{followUps.length}</Tag>
              </span>
            }
            key="followups"
            style={{ flex: 1, overflow: 'auto', padding: '12px 14px' }}
          >
            {followUps.map(fu => (
              <Card
                key={fu.id}
                size="small"
                style={{ background: '#0d1117', border: '1px solid #21262d', marginBottom: 8 }}
              >
                <Text style={{ color: '#c9d1d9', fontSize: 12 }}>{fu.message}</Text>
                {fu.answer && (
                  <Paragraph
                    style={{ color: '#8b949e', fontSize: 11, marginTop: 6, marginBottom: 0 }}
                  >
                    {fu.answer}
                  </Paragraph>
                )}
                <Tag
                  style={{ marginTop: 6, fontSize: 10 }}
                  color={fu.status === 'answered' ? 'success' : fu.status === 'task_created' ? 'processing' : 'error'}
                >
                  {fu.status}
                </Tag>
              </Card>
            ))}
          </TabPane>
        )}
      </Tabs>

      {/* Follow-up action card */}
      {TERMINAL_STATUSES.has(taskStatus) && (
        <div style={{ padding: '10px 14px', borderTop: '1px solid #21262d', flexShrink: 0 }}>
          <FollowUpCard
            taskId={taskId}
            taskGoal={taskGoal}
            taskStatus={taskStatus}
            onSubmit={onFollowUp}
            submitting={followUpSubmitting}
          />
        </div>
      )}
    </div>
  );
};

export default TaskDetailRail;
